import { useState, type FormEvent } from "react";
import { Aviso, Botao, Campo, Cartao } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  CAMPOS_DE_PESO,
  ROTULO_DO_SINAL,
  corpoDaConfiguracao,
  formularioDaConfiguracao,
  useConfiguracaoDaSaude,
  useSalvarConfiguracaoDaSaude,
  validarConfiguracao,
  type ConfiguracaoDaSaude,
  type FormularioDaConfiguracao,
} from "../../nucleo/sucesso";
import { useVoltarParaHome } from "../../nucleo/voltarParaHome";

/** Os pesos de cada sinal e os cortes das faixas. Só quem gerencia edita. */
export function PainelDosPesos({ podeGerenciar }: { podeGerenciar: boolean }) {
  const configuracao = useConfiguracaoDaSaude();
  if (configuracao.isPending) return <p role="status">Carregando os pesos…</p>;
  if (configuracao.isError) return <Aviso tom="erro">{mensagemDoConsole(configuracao.error)}</Aviso>;
  return <FormularioDosPesos configuracao={configuracao.data} podeGerenciar={podeGerenciar} />;
}

function FormularioDosPesos({
  configuracao,
  podeGerenciar,
}: {
  configuracao: ConfiguracaoDaSaude;
  podeGerenciar: boolean;
}) {
  const [form, setForm] = useState<FormularioDaConfiguracao>(() => formularioDaConfiguracao(configuracao));
  const [problema, setProblema] = useState<string | null>(null);
  const salvar = useSalvarConfiguracaoDaSaude();
  const voltarParaHome = useVoltarParaHome();

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const erro = validarConfiguracao(form);
    setProblema(erro);
    if (erro) return;
    salvar.mutate(corpoDaConfiguracao(form), {
      onSuccess: () => voltarParaHome("Pesos e faixas da saúde salvos."),
    });
  }

  return (
    <Cartao titulo="Pesos e faixas" data-testid="painel-dos-pesos">
      <p className="mb-3">
        A nota de cada sinal vai de 0 a 100. O score é a média das notas, cada uma multiplicada pelo
        peso. Sinal sem dado (por exemplo, cliente sem pesquisa respondida) não entra na média, e
        peso 0 tira o sinal da conta.
      </p>
      <form onSubmit={enviar} noValidate>
        <fieldset disabled={!podeGerenciar} className="grid gap-3 md:grid-cols-3">
          <legend className="campo__rotulo">Peso de cada sinal (0 a 100)</legend>
          {CAMPOS_DE_PESO.map(({ campo, sinal }) => (
            <Campo
              key={campo}
              rotulo={ROTULO_DO_SINAL[sinal]}
              inputMode="numeric"
              value={form[campo]}
              onChange={(e) => setForm({ ...form, [campo]: e.target.value })}
            />
          ))}
        </fieldset>
        <fieldset disabled={!podeGerenciar} className="grid gap-3 md:grid-cols-2 mt-3">
          <legend className="campo__rotulo">Cortes das faixas</legend>
          <Campo
            rotulo="Saudável a partir de"
            inputMode="numeric"
            value={form.limite_saudavel}
            onChange={(e) => setForm({ ...form, limite_saudavel: e.target.value })}
          />
          <Campo
            rotulo="Atenção a partir de (abaixo disso é risco)"
            inputMode="numeric"
            value={form.limite_atencao}
            onChange={(e) => setForm({ ...form, limite_atencao: e.target.value })}
          />
        </fieldset>
        {problema ? <Aviso tom="erro" className="mt-3">{problema}</Aviso> : null}
        {salvar.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(salvar.error)}</Aviso> : null}
        {podeGerenciar ? (
          <Botao type="submit" className="mt-3" disabled={salvar.isPending} data-testid="salvar-pesos">
            Salvar pesos
          </Botao>
        ) : (
          <p className="nota-rodape mt-3">Só a administração altera os pesos.</p>
        )}
        {configuracao.atualizada_por_nome ? (
          <p className="nota-rodape mt-2">Última alteração por {configuracao.atualizada_por_nome}.</p>
        ) : null}
      </form>
    </Cartao>
  );
}
